<template>
  <div class="pa-3 hyp-main-container">
    <SearchFilterSavedQueries
      :initialize="rememberedSavedSearchId"
      :reset="resetTimestamp"
      :selected="selectedSavedSearch"
      :defaultSavedSearchId="defaultSavedSearchId"
      @change="handleChangeSavedSearchQuery"
      @clear="handleClickClear"
      @show-library="handleClickShowLibrary"
      class="mx-3"
    ></SearchFilterSavedQueries>

    <div v-if="lockFacetPanels" class="mb-3">
      <v-icon size="11" class="mt-2" style="top: -0.5em;"
        >fas fa-spinner fa-spin</v-icon
      >
      Preparing Search Facets
    </div>

    <v-expansion-panels
      v-model="filterPanelsOpen"
      accordion
      class="hyp-main-panels-container"
    >
      <v-expansion-panel
        v-if="isSeeristNLQUnlocked"
        :disabled="lockFacetPanels"
      >
        <v-expansion-panel-header class="panel-header justify-self-start">
          <div>
            <span class="panel-header-text">Seerist NLQ</span>
            <SelectorButtonIcon
              icon="beta"
              :hover-text="nlqHelpText"
            ></SelectorButtonIcon>
            <div class="clearfix"></div>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <SearchNLQ
            :lastReset="resetTimestamp"
            @load-nlq-params="loadNLQParams"
          />
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel v-else></v-expansion-panel>

      <v-expansion-panel :disabled="lockFacetPanels">
        <v-expansion-panel-header class="panel-header justify-self-start">
          <div>
            <span class="panel-header-text">Keywords</span>
            <v-badge
              v-if="hasKeywordsFilterSet"
              title="There is an active filter in this panel"
              bordered
              inline
              class="ml-1"
            ></v-badge>
            <div class="clearfix"></div>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <SearchFilterKeywords
            @change-keywords="handleChangeKeywords"
            @clear-keywords="handleClearKeywords"
            @change-keywords-builder="handleChangeKeywordsBuilder"
            :inherited-keywords="keywords"
            :inherited-keywords-builder="keywordsBuilder"
            :lastReset="resetTimestamp"
          />
        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- LOCATION FILTER -->
      <v-expansion-panel :disabled="lockFacetPanels">
        <v-expansion-panel-header class="panel-header justify-self-start">
          <div>
            <span class="panel-header-text">Locations</span>
            <v-badge
              v-if="
                (locationSelectionAoiId && locationSelectionAoiId.length) ||
                (locationSelectionPlaces && locationSelectionPlaces.length) ||
                (locationSelectionAssets && locationSelectionAssets.length) ||
                locationSelectionPolygon
              "
              :title="`There are active location filters in this panel`"
              bordered
              inline
              class="ml-1"
            ></v-badge>
            <div class="clearfix"></div>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <SearchFilterLocations
            :incoming-bounds-type="boundsType"
            :incoming-aoi-id="locationSelectionAoiId"
            :incoming-places="locationSelectionPlaces"
            :incoming-assets="locationSelectionAssets"
            :selected-units-radius="selectionUnitsRadius"
            :incoming-polygon="locationSelectionPolygon"
            :reset="resetTimestamp"
            :selector-panel-is-open="selectorPanelIsOpen"
            :location-facet-is-open="isLocationFacetOpen"
            @change="handleChangeLocation"
            @editing-polygon="updateIsEditingPolygon"
          ></SearchFilterLocations>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- EVENTS 2.0 FILTER -->
      <v-expansion-panel :disabled="lockFacetPanels">
        <v-expansion-panel-header class="panel-header justify-self-start">
          <div>
            <span class="panel-header-text">Events</span>

            <v-tooltip top open-delay="1000" max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <div v-on="on" v-bind="attrs" class="switch-w-tooltip">
                  <v-badge
                    v-if="contentType === 'events' && hasEventFiltersSet"
                    :title="`There are active event and/or source filters selected in this panel`"
                    bordered
                    inline
                    class="ml-1"
                  ></v-badge>
                  <v-badge
                    v-if="
                      contentType &&
                      contentType !== 'events' &&
                      hasEventFiltersSet
                    "
                    :title="`There are active event and/or source filters selected in this panel`"
                    bordered
                    inline
                    color="#d0d0d0"
                    class="ml-1"
                  ></v-badge>
                </div>
              </template>
              <span v-if="contentType === 'events'"
                ><span class="application-heading"
                  >Searching for Events only.</span
                >
                <br />
                To disable Events and show Analysis, enable an Analysis
                filter.<br />
                <em>Searching across all content types is coming in 2023.</em>
              </span>
              <span v-if="contentType !== 'events' && riskCategoriesSelection"
                ><span class="application-heading"
                  >Searching for Analysis only.</span
                ><br />
                Disable Analysis filters to search for Events.<br />
                <em
                  >Searching across all content types is coming in 2023.</em
                ></span
              >
            </v-tooltip>

            <div class="clearfix"></div>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <SearchFilterEvents
            :reset="resetTimestamp"
            @change-events="handleChangeEventsV2"
            @change-hotspots-filter="handleChangeHotspotsFilter"
            @change-sources="handleChangeSource"
            @change-severity="handleChangeSeverity"
            @change-sentiment="handleChangeSentiment"
            @change-emotions="handleChangeEmotions"
            @change-geolocation-precisions="handleChangeGeolocationPrecisions"
            @change-tab="handleChangeEventType"
            :for-live-search="true"
            :topic-filter-counts="topicFilterCounts"
            :selected-geolocation-precisions="geolocationPrecisionsSelection"
            :geolocation-precision-filter-counts="
              geolocationPrecisionFilterCounts
            "
            :selected-topics="eventsSelection"
            :verified-type-selection="verifiedTypeSelection"
            :verified-extras-selection="verifiedExtrasSelection"
            :map-type="mapType"
            :events-type-selection="eventsTypeSelection"
            :hotspots-only-selection="hotspotsOnlySelection"
            :source-filter-counts="sourceFilterCounts"
            :sentiment-filter-counts="sentimentFilterCounts"
            :emotions-filter-counts="emotionsFilterCounts"
            :selected-sentiment="sentiment"
            :selected-emotions="emotions"
            :selected-sources="sources"
            :selected-severity="severity"
            @changed-live-search="handleChangeEvents"
            @change-has-topics-selected="handleChangeHasTopicsSelected"
          ></SearchFilterEvents>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- Source FILTER -->
      <v-expansion-panel
        v-if="!hasEssentials && isSourceTypeFacetFeatureUnlocked"
        :disabled="lockFacetPanels"
      >
        <v-expansion-panel-header class="panel-header justify-self-start">
          <div>
            <span class="panel-header-text">Source Type</span>
            <SelectorButtonIcon icon="beta"></SelectorButtonIcon>

            <v-tooltip top open-delay="1000" max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <div v-on="on" v-bind="attrs" class="switch-w-tooltip">
                  <v-badge
                    v-if="
                      contentType === 'events' &&
                      eventsTypeSelection !== 'verified' &&
                      (hasSourceMetadataFiltersSet ||
                        hasEmotionOrSentimentFiltersSet)
                    "
                    :title="`There are active event and/or source filters selected in this panel`"
                    bordered
                    inline
                    class="ml-1"
                  ></v-badge>
                  <v-badge
                    v-if="
                      ((contentType && contentType !== 'events') ||
                        (contentType === 'events' &&
                          eventsTypeSelection === 'verified')) &&
                      hasSourceMetadataFiltersSet
                    "
                    :title="`There are active event and/or source filters selected in this panel`"
                    bordered
                    inline
                    color="#d0d0d0"
                    class="ml-1"
                  ></v-badge>
                </div>
              </template>
            </v-tooltip>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <SearchFilterSourceType
            v-if="isSourceMetadataTypeFeatureUnlocked('type')"
            :selected="sourceMetadata.type"
            :counts="sourceMetadataFilterCounts.type"
            :reset="resetTimestamp"
            @change="handleChangeSourceMetadata"
          ></SearchFilterSourceType>
          <SearchFilterSourcePlatform
            v-if="isSourceMetadataTypeFeatureUnlocked('platform')"
            :selected="sourceMetadata.platform"
            :counts="sourceFilterCounts"
            :reset="resetTimestamp"
            @change="handleChangeSourceMetadata"
          ></SearchFilterSourcePlatform>
          <SearchFilterReliability
            v-if="isSourceMetadataTypeFeatureUnlocked('reliability')"
            :selected="sourceMetadata.reliability"
            :counts="sourceMetadataFilterCounts.reliability"
            :reset="resetTimestamp"
            @change="handleChangeSourceMetadata"
          ></SearchFilterReliability>
          <SearchFilterMisinfoSusceptibility
            v-if="isSourceMetadataTypeFeatureUnlocked('misinfo-susceptibility')"
            :selected="sourceMetadata.misinfoSusceptibility"
            :counts="sourceMetadataFilterCounts.misinfoSusceptibility"
            :reset="resetTimestamp"
            @change="handleChangeSourceMetadata"
          ></SearchFilterMisinfoSusceptibility>
          <SearchFilterSocialAuthenticity
            v-if="isSourceMetadataTypeFeatureUnlocked('social-authenticity')"
            :selected="sourceMetadata.socialAuthenticity"
            :counts="sourceMetadataFilterCounts.socialAuthenticity"
            :reset="resetTimestamp"
            @change="handleChangeSourceMetadata"
          ></SearchFilterSocialAuthenticity>
          <SearchFilterFollowerCount
            v-if="isSourceMetadataTypeFeatureUnlocked('social-follower-count')"
            :selected="sourceMetadata.followerCountBracket"
            :counts="sourceMetadataFilterCounts.followerCountBracket"
            :reset="resetTimestamp"
            @change="handleChangeSourceMetadata"
          ></SearchFilterFollowerCount>
          <SearchFilterLanguage
            v-if="isSourceMetadataTypeFeatureUnlocked('language')"
            :selected="sourceMetadata.language"
            :counts="sourceMetadataFilterCounts.language"
            :reset="resetTimestamp"
            @change="handleChangeSourceMetadata"
          ></SearchFilterLanguage>
          <SearchFilterCountryOf
            v-if="isSourceMetadataTypeFeatureUnlocked('country-of-origin')"
            type="origin"
            :selected="sourceMetadata.countryOfOrigin"
            :counts="sourceMetadataFilterCounts.countryOfOrigin"
            :reset="resetTimestamp"
            @change="handleChangeSourceMetadata"
          ></SearchFilterCountryOf>
          <SearchFilterCountryOf
            v-if="isSourceMetadataTypeFeatureUnlocked('country-of-focus')"
            type="focus"
            :selected="sourceMetadata.countryOfFocus"
            :counts="sourceMetadataFilterCounts.countryOfFocus"
            :reset="resetTimestamp"
            @change="handleChangeSourceMetadata"
          ></SearchFilterCountryOf>
          <SearchFilterSentiment
            v-if="isSourceTypeFacetMoveEmotionSentimentUnlocked"
            @change="handleChangeSentiment"
            :filter-counts="sentimentFilterCounts"
            :incoming-selection="sentiment"
          ></SearchFilterSentiment>
          <SearchFilterEmotions
            v-if="isSourceTypeFacetMoveEmotionSentimentUnlocked"
            @change="handleChangeEmotions"
            :filter-counts="emotionsFilterCounts"
            :incoming-selection="emotions"
          ></SearchFilterEmotions>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- ANALYSIS FILTER -->
      <v-expansion-panel :disabled="lockFacetPanels">
        <v-expansion-panel-header class="panel-header justify-self-start">
          <div>
            <span class="panel-header-text">Analysis</span>

            <v-tooltip top open-delay="1000" max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <div v-on="on" v-bind="attrs" class="switch-w-tooltip">
                  <v-badge
                    v-if="
                      contentType === 'analysis' &&
                      (riskCategoriesSelection || riskCategoryTagsSelection)
                    "
                    :title="`There are active analysis filters in this panel`"
                    bordered
                    inline
                    class="ml-1"
                  ></v-badge>
                  <v-badge
                    v-if="
                      contentType &&
                      contentType !== 'analysis' &&
                      eventsSelection
                    "
                    bordered
                    inline
                    color="#d0d0d0"
                    class="ml-1"
                  ></v-badge>
                </div>
              </template>
              <span v-if="contentType === 'analysis'"
                ><span class="application-heading"
                  >Searching for Analysis only.</span
                >
                <br />
                To disable Analysis and show Events, disable all Analysis
                filters.<br />
                <em>Searching across all content types is coming in 2023.</em>
              </span>
              <span v-if="contentType !== 'analysis'"
                ><span class="application-heading"
                  >Searching for Events only.</span
                ><br />
                Enable an Analysis filter to search for Analysis.<br />
                <em
                  >Searching across all content types is coming in 2023.</em
                ></span
              >
            </v-tooltip>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <SearchFilterAnalysis
            :reset="resetTimestamp"
            :risk-categories-filter-counts="riskCategoriesFilterCounts"
            :risk-categories-selection="riskCategoriesSelection"
            :risk-category-tags-selection="riskCategoryTagsSelection"
            @change-risk-categories="handleChangeRiskCategories"
            @change-risk-category-tags="handleChangeRiskCategoryTags"
          ></SearchFilterAnalysis>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- DATE RANGE PICKER FILTER -->
      <v-expansion-panel :disabled="lockFacetPanels">
        <v-expansion-panel-header
          class="panel-header justify-self-start date-range"
        >
          <div>
            <span class="panel-header-text">Date Range</span>
            <v-badge
              :content="getDateRangeString()"
              :title="`Results are limited to ${getDateRangeString()}`"
              bordered
              inline
              class="ml-1"
            ></v-badge>
            <div class="clearfix"></div>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <SearchFilterDateRangePicker
            :selected-range="timeRange"
            :reset="resetTimestamp"
            :date-range-max-date-back="dateRangeMaxDateBack"
            :date-range-start-days-back="dateRangeStartDaysBack"
            @change="handleChangeDateRange"
          ></SearchFilterDateRangePicker>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import lodashGet from "lodash/get";
import lodashHas from "lodash/has";
import lodashForEach from "lodash/forEach";
import lodashSortBy from "lodash/sortBy";
import lodashCloneDeep from "lodash/cloneDeep";
import lodashDebounce from "lodash/debounce";
import lodashUpperFirst from "lodash/upperFirst";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { ICONS } from "@/common/constants/icons";
import {
  CONTENT_TYPES,
  EVENT_ACTIONS,
  SEARCH_CATEGORIES,
} from "@/common/constants/analytics";
import {
  convertDatesToRange,
  getTodayStartEndDate,
} from "@/common/date.utilities";
import { mapGetters } from "vuex";
import {
  GET_PLACES,
  GET_SAVED_SEARCH_DETAILS_BY_ID,
  GET_SAVED_SEARCH_QUERIES,
} from "@/store/actions.type";
import {
  MODULE_PREFIX_HOME,
  MODULE_PREFIX_MAP,
  MODULE_PREFIX_PLACES,
  MODULE_PREFIX_SAVED_SEARCH,
  MODULE_PREFIX_USER,
} from "@/store";
import { MAIN_MAP_ID } from "@/common/constants/maps";
import { findTopicAllFlatten } from "@/services/events/topics.service";
import { SEARCH_LOCATION_RADIUS_DEFAULT } from "@/common/constants/savedSearch";
import { NOTIFICATION_ID_PROPERTY } from "@/common/constants/notifications";
import { NLQ_HELP_TEXT } from "@/common/constants/nlq";
import { hasEssentialsAccess } from "@/services/user/user.service";
import { transformRadiusToKm, isNonEmptyArray } from "@/common/basic.utilities";
import {
  getAssets,
  searchWithAssetRadius,
} from "@/services/assetManagement/assets.service";
import { SOURCE_METADATA } from "@/common/constants/search";

dayjs.extend(utc);

const EMITTED_ACTIONS = {
  CHANGE_KEYWORDS: "change-keywords",
  CHANGE_KEYWORDS_BUILDER: "change-keywords-builder",
  CHANGE_DATE_RANGE: "change-date-range",
  CHANGE_BOUNDS: "change-bounds",
  CHANGE_PLACES: "change-places",
  CHANGE_POLYGON: "change-polygon",
  CHANGE_LOCATIONS: "change-locations",
  CHANGE_EVENTS: "change-events",
  CHANGE_EVENTS_V2: "change-events-v2",
  CHANGE_SOURCES: "change-sources",
  CHANGE_HAS_FILTERS_SET: "change-has-filters-set",
  CHANGE_SHOULD_PULL_DATA: "change-should-pull-data",
  RESET: "reset",
  CHANGE_SAVED_SEARCH_QUERY: "change-saved-search-query",
  CHANGE_HOTSPOTS_FILTER: "change-hotspots-filter",
  CHANGE_TAB: "change-tab",
  CHANGE_RISK_CATEGORIES: "change-risk-categories",
  CHANGE_SEVERITY: "change-severity",
  CHANGE_SENTIMENT: "change-sentiment",
  CHANGE_EMOTIONS: "change-emotions",
  CHANGE_SOURCE_METADATA: "change-source-metadata",
  CHANGE_RISK_CATEGORY_TAGS: "change-risk-category-tags",
  CHANGE_GEOLOCATION_PRECISIONS: "change-geolocation-precisions",
  CLICK_CLEAR: "click-clear",
  SHOW_LIBRARY: "show-library",
  EDITING_POLYGON: "editing-polygon",
};

export default {
  name: "SearchContentFilters",
  components: {
    SearchFilterDateRangePicker: () =>
      import("@/components/search/SearchFilterDateRangePicker.vue"),
    SearchFilterEvents: () => import("@/components/search/SearchFilterEvents"),
    SearchFilterLocations: () =>
      import("@/components/search/SearchFilterLocations"),
    SearchFilterSavedQueries: () =>
      import("@/components/search/SearchFilterSavedQueries"),
    SearchFilterAnalysis: () =>
      import("@/components/search/SearchFilterAnalysis"),
    SearchFilterKeywords: () =>
      import("@/components/search/SearchFilterKeywords"),
    SearchNLQ: () => import("@/components/search/SearchNLQ"),
    SelectorButtonIcon: () => import("@/components/common/SelectorButtonIcon"),
    SearchFilterSourceType: () =>
      import("@/components/search/SearchFilterSourceType"),
    SearchFilterSourcePlatform: () =>
      import("@/components/search/SearchFilterSourcePlatform"),
    SearchFilterReliability: () =>
      import("@/components/search/SearchFilterReliability"),
    SearchFilterCountryOf: () =>
      import("@/components/search/SearchFilterCountryOf"),
    SearchFilterMisinfoSusceptibility: () =>
      import("@/components/search/SearchFilterMisinfoSusceptibility"),
    SearchFilterLanguage: () =>
      import("@/components/search/SearchFilterLanguage"),
    SearchFilterSentiment: () =>
      import("@/components/search/SearchFilterSentiment"),
    SearchFilterEmotions: () =>
      import("@/components/search/SearchFilterEmotions"),
    SearchFilterSocialAuthenticity: () =>
      import("@/components/search/SearchFilterSocialAuthenticity"),
    SearchFilterFollowerCount: () =>
      import("@/components/search/SearchFilterFollowerCount"),
  },
  props: {
    dateRangeStartDaysBack: {
      type: Number,
    },
    dateRangeMaxDateBack: {
      type: String,
    },
    filterCounts: {
      type: Object,
      default: () => {},
    },
    resetTimestamp: {
      type: String,
      default: new Date().toISOString(),
    },
    currentContentType: {
      type: String,
      default: CONTENT_TYPES.events,
    },
    selectedSearchParams: {
      type: Object,
    },
    selectorPanelIsOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const hasEssentials = hasEssentialsAccess();
    const sourceMetadata = {};
    SOURCE_METADATA.forEach((type) => {
      sourceMetadata[type] = [];
    });
    return {
      hasEssentials: hasEssentials,
      boundsType: "geography",
      keywords: null,
      keywordsBuilder: null,
      filterPanelsOpen: -1,
      loadingPrefs: false,
      loadingQueries: false,
      queriesHaveBeenLoaded: false,
      sources: null,
      severity: null,
      geolocationPrecisionsSelection: null,
      timeRange: null,
      hasTopicsSelected: false,
      hasEventsV2Selected: false,
      events: null,
      eventsSelection: null,
      eventsTypeSelection: null,
      verifiedTypeSelection: null,
      verifiedExtrasSelection: null,
      severityMin: null,
      severityMax: null,
      mapType: null,
      hotspotsOnlySelection: null,
      selectedSavedSearch: null,
      rememberedSavedSearchId: null,
      defaultSavedSearchId: this.$route.params?.searchid,
      placesSelection: null,
      assetsSelection: null,
      locationSelectionAoiId: null,
      locationSelectionPlaces: null,
      locationSelectionAssets: null,
      selectionUnitsRadius: {
        radius: SEARCH_LOCATION_RADIUS_DEFAULT,
        poisDistUnits: "mi",
      },
      locationSelectionPolygon: null,
      riskCategoriesSelection: null,
      hasRiskCategoriesSelected: false,
      contentType: null,
      riskCategoryTagsSelection: null,
      sentiment: null,
      emotions: null,
      sourceMetadata: sourceMetadata,
      nlqHelpText: NLQ_HELP_TEXT,
    };
  },
  async mounted() {
    await this.getPlaces();
    await this.loadQueries();

    // handle deep link in URL
    if (this.$route.params?.searchid) {
      if (
        this.$route.params?.searchid === "library" &&
        this.hasSearchLibraryAccess
      ) {
        this.$emit(EMITTED_ACTIONS.SHOW_LIBRARY, true);
      } else {
        this.rememberedSavedSearchId = this.$route.params.searchid;
        this.$store.dispatch(
          MODULE_PREFIX_SAVED_SEARCH + GET_SAVED_SEARCH_DETAILS_BY_ID,
          this.rememberedSavedSearchId
        );
        this.$emit(EMITTED_ACTIONS.CHANGE_HAS_FILTERS_SET, true);
      }
      return;
    }

    if (
      this.preferences &&
      this.preferences.liveSearch &&
      !this.selectedSearchParams
    ) {
      if (
        this.preferences.liveSearch.selectedSavedSearch &&
        !this.preferences.liveSearch.searchLibraryName
      ) {
        this.rememberedSavedSearchId = this.preferences.liveSearch.selectedSavedSearch;
        this.$store.dispatch(
          MODULE_PREFIX_SAVED_SEARCH + GET_SAVED_SEARCH_DETAILS_BY_ID,
          this.rememberedSavedSearchId
        );
        return;
      }

      await this.setSelectionsWithPreferences();
    } else {
      this.$emit(EMITTED_ACTIONS.CHANGE_SHOULD_PULL_DATA, false);
    }

    this.$emit(EMITTED_ACTIONS.CHANGE_HAS_FILTERS_SET, this.hasFiltersSet);
  },
  computed: {
    ...mapGetters({
      queries: MODULE_PREFIX_SAVED_SEARCH + "queries",
      maps: MODULE_PREFIX_MAP + "maps",
      preferences: MODULE_PREFIX_USER + "preferences",
      places: MODULE_PREFIX_PLACES + "places",
      savedSearchDetails: MODULE_PREFIX_SAVED_SEARCH + "savedSearchDetails",
      selectorPanel: MODULE_PREFIX_HOME + "selectorPanel",
    }),
    isSeeristNLQUnlocked() {
      return this.featureUnlocked("seerist-nlq");
    },
    isSourceTypeFacetFeatureUnlocked() {
      return this.featureUnlocked("livesearch-facet-source-type");
    },
    isSourceTypeFacetMoveEmotionSentimentUnlocked() {
      return this.featureUnlocked(
        "livesearch-facet-source-type-move-emotion-sentiment"
      );
    },
    hasSearchLibraryAccess() {
      return !hasEssentialsAccess();
    },
    selectedData() {
      return this.selectorPanel.selectedData;
    },
    prefDistanceUnits() {
      const unit = this.preferences?.poisDistUnits;
      return unit ? (unit === "miles" ? "mi" : "km") : "mi";
    },
    hasPlaces() {
      return (
        this.places && this.places.length && !this.places[0].isDefaultLayer
      );
    },
    imgSrc() {
      if (
        lodashGet(ICONS, "misc.datePicker.inactive") &&
        lodashGet(ICONS, "menu.events.inactive")
      ) {
        const dateRange = ICONS.misc.datePicker.inactive;
        const events = ICONS.menu.events.inactive;
        return {
          dateRange: require(`@/assets/images${dateRange}`),
          events: require(`@/assets/images${events}`),
        };
      }
      return null;
    },
    sourceFilterCounts() {
      return lodashGet(this.filterCounts, "sourceTotals");
    },
    topicFilterCounts() {
      return lodashGet(this.filterCounts, "topicTotals");
    },
    geolocationPrecisionFilterCounts() {
      return lodashGet(this.filterCounts, "geolocationPrecisionTotals");
    },
    sentimentFilterCounts() {
      return lodashGet(this.filterCounts, "sentimentTotals");
    },
    emotionsFilterCounts() {
      return lodashGet(this.filterCounts, "emotionTotals");
    },
    sourceMetadataFilterCounts() {
      const counts = {};
      SOURCE_METADATA.forEach((type) => {
        counts[type] = this.filterCounts?.[
          `sourceMetadata${lodashUpperFirst(type)}Totals`
        ];
      });
      return counts;
    },
    hasFiltersSet() {
      if (this.hotspotsOnlySelection) {
        return true;
      }
      if (
        this.eventsTypeSelection !== (this.hasEssentials ? "verified" : "all")
      ) {
        return true;
      }
      if (this.hasTopicsSelected) {
        return true;
      }
      if (this.hasEventsV2Selected) {
        return true;
      }
      if (this.hasKeywordsFilterSet) {
        return true;
      }
      if (this.sources && Object.values(this.sources).includes(true)) {
        return true;
      }
      if (this.placesSelection) {
        return true;
      }
      if (this.assetsSelection) {
        return true;
      }
      if (
        this.locationSelectionPlaces ||
        this.locationSelectionAssets ||
        this.locationSelectionAoiId
      ) {
        return true;
      }
      if (this.selectionUnitsRadius) {
        return true;
      }
      if (this.boundsType === "mapextent" || this.boundsType === "worldwide") {
        return true;
      }
      if (
        this.timeRange &&
        JSON.stringify(this.timeRange) !== JSON.stringify([-7, 0])
      ) {
        return true;
      }
      if (this.selectedSavedSearch) {
        return true;
      }
      if (this.riskCategoriesSelection) {
        return true;
      }
      if (this.riskCategoryTagsSelection) {
        return true;
      }
      return false;
    },
    shouldPullData() {
      if (this.hasTopicsSelected) {
        return true;
      }
      if (this.hasEventsV2Selected) {
        return true;
      }
      if (this.hasKeywordsFilterSet) {
        return true;
      }
      if (this.sources && Object.values(this.sources).includes(true)) {
        return true;
      }
      if (
        this.timeRange &&
        JSON.stringify(this.timeRange) !== JSON.stringify([-7, 0])
      ) {
        return true;
      }
      if (this.selectedSavedSearch) {
        return true;
      }
      if (this.riskCategoryTagsSelection) {
        return true;
      }
      return false;
    },
    filterResetIconSrc() {
      return require("@/assets/images/icons/reset_filters.png");
    },
    map() {
      return this.maps[MAIN_MAP_ID];
    },
    riskCategoriesFilterCounts() {
      return lodashGet(this.filterCounts, "riskCategoryTotals");
    },
    hasKeywordsFilterSet() {
      return (
        !!lodashGet(this.keywordsBuilder, "includes[0].keywords.length") ||
        !!lodashGet(this.keywordsBuilder, "excludes.length") ||
        !!this.keywords
      );
    },
    hasEmotionOrSentimentFiltersSet() {
      return this.emotions?.length || this.sentiment?.length;
    },
    hasSourceMetadataFiltersSet() {
      if (this.sourceMetadata) {
        return [
          this.sourceMetadata.type,
          this.sourceMetadata.platform,
          this.sourceMetadata.reliability,
          this.sourceMetadata.misinfoSusceptibility,
          this.sourceMetadata.socialAuthenticity,
          this.sourceMetadata.followerCountBracket,
          this.sourceMetadata.language,
          this.sourceMetadata.countryOfOrigin,
          this.sourceMetadata.countryOfFocus,
        ].some(isNonEmptyArray);
      }
      return false;
    },
    hasEventFiltersSet() {
      //This isn't exhaustive. Ignoring verified event specific stuff like assets.
      //Since they require a different tab selected anyhow

      if (
        this.severity &&
        ((!isNaN(this.severity.severityMax) &&
          this.severity.severityMax < 10) ||
          (!isNaN(this.severity.severityMin) && this.severity.severityMin > 0))
      ) {
        return true;
      }

      if (
        Array.isArray(this.geolocationPrecisionsSelection) &&
        this.geolocationPrecisionsSelection.length > 0
      ) {
        return true;
      }

      if (Array.isArray(this.emotions) && this.emotions.length > 0) {
        return true;
      }
      if (Array.isArray(this.sentiment) && this.sentiment.length > 0) {
        return true;
      }

      SOURCE_METADATA.forEach((type) => {
        if (
          Array.isArray(this.sourceMetadata?.[type]) &&
          this.sourceMetadata[type].length > 0
        ) {
          return true;
        }
      });

      if (this.hotspotsOnlySelection) {
        return true;
      }

      if (
        this.eventsTypeSelection !== (this.hasEssentials ? "verified" : "all")
      ) {
        return true;
      }
      if (this.hasTopicsSelected) {
        return true;
      }
      if (this.hasEventsV2Selected) {
        return true;
      }
      if (this.sources && Object.values(this.sources).includes(true)) {
        return true;
      }
      return false;
    },
    lockFacetPanels() {
      return (
        this.loadingQueries || this.loadingPrefs || !this.queriesHaveBeenLoaded
      );
    },
    isLocationFacetOpen() {
      let locationFacetIndex = 2;
      // nlq has a v-else so it doesn't count
      return this.filterPanelsOpen === locationFacetIndex;
    },
  },
  methods: {
    async loadQueries(skipGet) {
      this.loadingQueries = true;
      let SORTED = [];
      try {
        if (!skipGet) {
          await this.$store.dispatch(
            MODULE_PREFIX_SAVED_SEARCH + GET_SAVED_SEARCH_QUERIES,
            {}
          );
        }
        SORTED = lodashSortBy(this.queries, "name");
      } catch (error) {
        console.error("Error in loadQueries: " + JSON.stringify(error));
      }
      this.savedQueries = SORTED;
      this.loadingQueries = false;
    },
    async getPlaces() {
      if (!this.places || !this.places.length) {
        await this.$store.dispatch(MODULE_PREFIX_PLACES + GET_PLACES);
      }
    },
    isSourceMetadataTypeFeatureUnlocked(type) {
      return this.featureUnlocked(`livesearch-source-metadata-${type}`);
    },
    async setSelectionsWithPreferences() {
      let lsPrefs = this.preferences.liveSearch;

      if (lsPrefs.riskCategories) {
        this.contentType = CONTENT_TYPES.analysis;
        this.riskCategoriesSelection = lsPrefs.riskCategories;
        this.$emit(
          EMITTED_ACTIONS.CHANGE_RISK_CATEGORIES,
          this.riskCategoriesSelection
        );
      } else {
        this.riskCategoriesSelection = null;
      }

      if (lsPrefs.tags) {
        this.riskCategoryTagsSelection = lsPrefs.tags;
        this.$emit(
          EMITTED_ACTIONS.CHANGE_RISK_CATEGORY_TAGS,
          this.riskCategoryTagsSelection
        );
      } else {
        this.riskCategoryTagsSelection = null;
      }

      if (lsPrefs.boundsType) {
        this.boundsType = lsPrefs.boundsType;
        this.$emit(EMITTED_ACTIONS.CHANGE_BOUNDS, this.boundsType, true);
      }

      if (
        lsPrefs.aoiId ||
        lsPrefs.places ||
        lsPrefs.assets ||
        lsPrefs.polygon
      ) {
        const places = this.getSelectedPlacesFeatures(lsPrefs.places);
        const assets = await this.getSelectedAssetsFeatures(lsPrefs.assets);
        this.$emit(EMITTED_ACTIONS.CHANGE_LOCATIONS, {
          boundsType: lsPrefs.boundsType,
          bounds: lsPrefs.boundsType === "mapextent" ? this.bounds : null,
          places: places,
          assets: assets,
          radius: lsPrefs.placesRadius,
          poisDistUnits: lsPrefs.placesPoisDistUnits,
          aoiId: lsPrefs.aoiId,
          polygon: lsPrefs.polygon,
        });
        this.locationSelectionAoiId = lsPrefs.aoiId;
        this.locationSelectionPlaces = lsPrefs.places;
        this.locationSelectionAssets = assets;
        this.locationSelectionPolygon = lsPrefs.polygon;

        this.selectionUnitsRadius = {};
        this.selectionUnitsRadius.radius = lsPrefs.placesRadius;
        this.selectionUnitsRadius.poisDistUnits = lsPrefs.placesPoisDistUnits;
      }

      if (lsPrefs.sources) {
        this.sources = {};
        if (this.featureUnlocked("livesearch-source-metadata-platform")) {
          let individualSources = lsPrefs.sources.split(",");
          for (let source of individualSources) {
            this.sources[source] = true;
          }
        } else {
          let constructingSources = {
            social: false,
            news: false,
          };
          lodashForEach(lsPrefs.sources.split(","), (source) => {
            if (!this.featureUnlocked("livesearch-source-metadata-platform")) {
              if (source === "twitter") {
                constructingSources.social = true;
              }
              if (source === "news") {
                constructingSources.news = true;
              }
            }
          });
          this.sources = constructingSources;
        }
        this.$emit(EMITTED_ACTIONS.CHANGE_SOURCES, this.sources);
      }

      if (lsPrefs.geolocationPrecisions) {
        this.geolocationPrecisionsSelection = lsPrefs.geolocationPrecisions;
        this.$emit(
          EMITTED_ACTIONS.CHANGE_GEOLOCATION_PRECISIONS,
          this.geolocationPrecisionsSelection
        );
      }

      //Prefs for keywords
      if (
        lodashGet(lsPrefs.keywordsBuilder, "includes[0].keywords.length") ||
        lodashGet(lsPrefs.keywordsBuilder, "excludes.length")
      ) {
        this.keywordsBuilder = lsPrefs.keywordsBuilder;
        this.$emit(
          EMITTED_ACTIONS.CHANGE_KEYWORDS_BUILDER,
          this.keywordsBuilder
        );
      } else if (lsPrefs.search) {
        this.keywords = lsPrefs.search;
        this.$emit(EMITTED_ACTIONS.CHANGE_KEYWORDS, this.keywords);
      }

      if (lsPrefs.topic || lsPrefs.topics) {
        this.eventsSelection = lsPrefs.topic || lsPrefs.topics;
      }
      if (lsPrefs.verifiedType) {
        this.verifiedTypeSelection = lsPrefs.verifiedType;
        this.verifiedExtrasSelection = {
          sectors: lsPrefs.sectors || null,
          veAssets: lsPrefs.veAssets || null,
          attackTypes: lsPrefs.attackTypes || null,
          perpetrators: lsPrefs.perpetrators || null,
        };
      }

      if (
        lodashHas(lsPrefs, "severityMin") ||
        lodashHas(lsPrefs, "severityMax")
      ) {
        this.severity = {
          severityMin: lsPrefs.severityMin || 0,
          severityMax: lsPrefs.severityMax || 10,
        };
      }

      if (lsPrefs.mapType) {
        this.mapType = lsPrefs.mapType;
      }

      if (lsPrefs.geolocationPrecisions) {
        this.geolocationPrecisionsSelection = lsPrefs.geolocationPrecisions;
      }

      if (lsPrefs.sentiment) {
        this.handleChangeSentiment(lsPrefs.sentiment);
      }

      if (lsPrefs.emotions) {
        this.handleChangeEmotions(lsPrefs.emotions);
      }

      this.handleLoadSourceMetadata(lsPrefs.sourceMetadata);

      this.eventsTypeSelection = lsPrefs.eventType || "all";
      this.hotspotsOnlySelection = !!lsPrefs.hotspotsOnly || false;
      this.riskCategoriesSelection = lsPrefs.riskCategories || null;
      this.riskCategoryTagsSelection = lsPrefs.tags || null;
      this.selectedSavedSearch = null;

      this.handleChangeEventsV2({
        eventType: this.eventsTypeSelection,
        events: this.eventsSelection,
        veAssets: this.verifiedExtrasSelection
          ? this.verifiedExtrasSelection.veAssets
          : null,
        sectors: this.verifiedExtrasSelection
          ? this.verifiedExtrasSelection.sectors
          : null,
        attackTypes: this.verifiedExtrasSelection
          ? this.verifiedExtrasSelection.attackTypes
          : null,
        perpetrators: this.verifiedExtrasSelection
          ? this.verifiedExtrasSelection.perpetrators
          : null,
        //TODO maybe this could be constructed better?
        severity:
          lodashHas(this, "severityMin") || lodashHas(this, "severityMax")
            ? [this.severityMin, this.severityMax]
            : null,
        verifiedType: this.verifiedTypeSelection,
        mapType: this.mapType,
        riskCategoriesSelection: this.riskCategoriesSelection,
      });
    },
    handleChangeKeywords(keywords) {
      this.keywords = keywords;
      this.$emit(EMITTED_ACTIONS.CHANGE_KEYWORDS, this.keywords);
    },
    handleClearKeywords() {
      this.handlePageTrackerEvent(
        SEARCH_CATEGORIES.live_search_keyword,
        EVENT_ACTIONS.clear,
        "Clear"
      );
    },
    handleChangeSource(sources) {
      this.sources = sources;
      this.$emit(EMITTED_ACTIONS.CHANGE_SOURCES, sources);
      if (sources) {
        for (const source in this.sources) {
          if (source) {
            this.handlePageTrackerEvent(
              SEARCH_CATEGORIES.live_search_sources,
              EVENT_ACTIONS.clicked,
              source
            );
          }
        }
      }
    },
    handleChangeDateRange(dates, dontPullData) {
      this.timeRange = dates.range;
      this.$emit(EMITTED_ACTIONS.CHANGE_DATE_RANGE, dates, dontPullData);
      if (dates.start) {
        this.handlePageTrackerEvent(
          SEARCH_CATEGORIES.live_search_start_date,
          EVENT_ACTIONS.changed,
          "start date to " + dates.start
        );
      }
      if (dates.end) {
        this.handlePageTrackerEvent(
          SEARCH_CATEGORIES.live_search_end_date,
          EVENT_ACTIONS.changed,
          "end date to " + dates.end
        );
      }
    },
    handleChangeBounds() {
      this.$emit(EMITTED_ACTIONS.CHANGE_BOUNDS, {
        boundsType: this.boundsType,
        bounds: this.bounds,
      });
      this.handlePageTrackerEvent(
        SEARCH_CATEGORIES.live_search_bounds,
        EVENT_ACTIONS.changed,
        "bounds to " + this.bounds
      );
      this.handlePageTrackerEvent(
        SEARCH_CATEGORIES.live_search_bounds_type,
        EVENT_ACTIONS.changed,
        "boundsType to " + this.boundsType
      );
    },
    getSelectedPlacesFeatures(selected) {
      const places = [];
      this.places.forEach((layer) => {
        if (lodashGet(layer, "data.features") && layer.data.features.length) {
          const len = layer.data.features.length;
          for (let i = 0; i < len; i++) {
            const place = layer.data.features[i];
            place.properties["map_icon_name"] = layer.icons
              ? layer.icons
              : "default";
            place.properties["map_layer_name"] = layer.name ? layer.name : null;
            if (
              lodashGet(place, "id") &&
              selected &&
              selected.includes(place.id) &&
              lodashGet(place, "geometry.coordinates")
            ) {
              places.push(place);
            }
          }
        }
      });
      return places;
    },
    async getSelectedAssetsFeatures(selected) {
      let assets = [];
      if (selected && selected.length) {
        try {
          const options = {
            filterAssetIds: selected.join(),
            titleSortDirection: "ASC",
            fields: ["id", "title", "assetType", "latitude", "longitude"],
            itemsPerPage: 50,
          };
          const results = await getAssets({ options });
          if (results?.assets?.length) {
            assets = results.assets;
          }
        } catch (error) {
          console.error(
            "Error fetching assets in getSelectedAssetsFeatures:",
            error
          );
        }
      }
      return assets;
    },
    handleChangeLocation(params) {
      let placesIds = null;
      let places = null;
      let radius = null;
      let poisDistUnits = null;

      if (params.places && params.places.length) {
        if (Array.isArray(params.places[0])) {
          placesIds = params.places.map((place) => {
            radius = place[2] || SEARCH_LOCATION_RADIUS_DEFAULT;
            poisDistUnits = place[4] || this.prefDistanceUnits;
            return place[3] || null;
          });
          radius = transformRadiusToKm(radius, poisDistUnits);
          poisDistUnits = "km";
        } else {
          placesIds = params.places;
        }
        places = this.getSelectedPlacesFeatures(placesIds);
      }
      this.locationSelectionPlaces = placesIds;

      if (params.assets && params.assets.length) {
        radius = transformRadiusToKm(params.radius, params.poisDistUnits);
        poisDistUnits = "km";
      }
      if (radius) {
        this.selectionUnitsRadius = {};
        this.$set(this.selectionUnitsRadius, "radius", radius);
        this.$set(this.selectionUnitsRadius, "poisDistUnits", poisDistUnits);
      }
      this.locationSelectionAssets = params.assets;
      this.locationSelectionAoiId = params.aoiId;
      this.locationSelectionPolygon = params.polygon;

      this.$emit(EMITTED_ACTIONS.CHANGE_LOCATIONS, {
        boundsType: params.boundsType,
        bounds: params.boundsType === "mapextent" ? this.bounds : null,
        places: places,
        assets: params.assets,
        radius:
          radius || transformRadiusToKm(params.radius, params.poisDistUnits),
        poisDistUnits: poisDistUnits || "km",
        aoiId: params.aoiId,
        polygon: params.polygon,
      });
      this.handlePageTrackerEvent(
        SEARCH_CATEGORIES.live_search_places,
        EVENT_ACTIONS.changed,
        "location changed"
      );
    },
    handleChangePlaces(params) {
      this.placesSelection = params.places;
      const places = this.getSelectedPlacesFeatures(params.places);
      this.$emit(EMITTED_ACTIONS.CHANGE_PLACES, {
        places: places,
        radius: params.radius,
        poisDistUnits: params.poisDistUnits,
      });
      this.handlePageTrackerEvent(
        SEARCH_CATEGORIES.live_search_places,
        EVENT_ACTIONS.changed,
        "places changed"
      );
    },
    updateIsEditingPolygon(isEditing) {
      this.$emit(EMITTED_ACTIONS.EDITING_POLYGON, isEditing);
    },
    handleChangeEvents(event, events, eventType, mapType) {
      //todo update eventsSelection here?
      this.$emit(EMITTED_ACTIONS.CHANGE_EVENTS, events, eventType, mapType);
      let evt = events[events.length - 1];
      this.handlePageTrackerEvent(
        SEARCH_CATEGORIES.live_search_events,
        EVENT_ACTIONS.clicked,
        evt
      );
    },
    handleChangeHasTopicsSelected(val) {
      this.hasTopicsSelected = val;
    },
    handleChangeEventsV2(params) {
      let hasEventsV2Selected = false;
      if (params) {
        if (params.riskCategoriesSelection) {
          hasEventsV2Selected = false;
        } else if (params.events && params.events.length > 0) {
          hasEventsV2Selected = true;
        } else if (params.sectors && params.sectors.length > 0) {
          hasEventsV2Selected = true;
        } else if (params.veAssets && params.veAssets.length > 0) {
          hasEventsV2Selected = true;
        }
      }
      this.hasEventsV2Selected = hasEventsV2Selected;
      if (!this.hotspotsOnlySelection) {
        this.eventsTypeSelection = params.eventType;
      }

      if (!params.riskCategoriesSelection) {
        this.mapType = params.mapType || this.mapType;

        this.$emit(EMITTED_ACTIONS.CHANGE_EVENTS_V2, params);
        this.eventsSelection = params.events;
      }
    },
    handleChangeHotspotsFilter(params) {
      this.hotspotsOnlySelection = params.hotspotsOnly || false;

      this.$emit(EMITTED_ACTIONS.CHANGE_HOTSPOTS_FILTER, params);
      if (params.hotspotsOnly) {
        this.handlePageTrackerEvent(
          SEARCH_CATEGORIES.live_search_sources,
          EVENT_ACTIONS.clicked,
          "hotspots only"
        );
      }
    },
    reset(dontSendEmit) {
      this.keywords = "";
      this.keywordsBuilder = null;
      this.boundsType = "geography";
      this.locationSelectionPlaces = null;
      this.locationSelectionAoiId = null;
      this.locationSelectionAssets = null;
      this.radius = null;
      this.assetsSelection = null;
      this.locationSelectionPolygon = null;
      this.placesSelection = null;
      this.sources = {};
      this.eventsSelection = "";
      this.hotspotsOnlySelection = false;
      this.eventsTypeSelection = "all";
      this.verifiedTypeSelection = null;
      this.verifiedExtrasSelection = null;
      this.mapType = "marker";
      this.geolocationPrecisionsSelection = [];
      this.riskCategoriesSelection = null;
      this.riskCategoryTagsSelection = null;
      this.selectedSavedSearch = null;

      this.handleChangeSeverity(null, dontSendEmit);
      this.handleChangeSentiment([], dontSendEmit);
      this.handleChangeEmotions([], dontSendEmit);

      //source metadata
      this.handleLoadSourceMetadata({}, dontSendEmit);

      if (!dontSendEmit) {
        this.$emit(EMITTED_ACTIONS.CHANGE_KEYWORDS, "");
        this.$emit(EMITTED_ACTIONS.CHANGE_KEYWORDS_BUILDER, null);
        this.$emit(EMITTED_ACTIONS.CHANGE_BOUNDS, this.boundsType);
        this.$emit(EMITTED_ACTIONS.CHANGE_LOCATIONS, {
          aoiId: null,
          places: null,
          radius: null,
          assets: null,
          poisDistUnits: null,
          polygon: null,
        });
        this.$emit(EMITTED_ACTIONS.CHANGE_PLACES, {
          radius: null,
          places: null,
          poisDistUnits: null,
        });
        this.$emit(EMITTED_ACTIONS.CHANGE_GEOLOCATION_PRECISIONS, null);
        this.$emit(EMITTED_ACTIONS.CHANGE_RISK_CATEGORIES, null);
        this.$emit(EMITTED_ACTIONS.CHANGE_RISK_CATEGORY_TAGS, null);
        this.$emit(EMITTED_ACTIONS.CHANGE_SAVED_SEARCH_QUERY, null);
      }
    },
    loadNLQParams(params) {
      //Just to make sure all settings are reset before NLQ applied
      this.$emit(EMITTED_ACTIONS.RESET, {});
      lodashDebounce(() => {
        this.handleChangeSavedSearchQuery(params, true);
      }, 1000)();
    },
    async handleChangeSavedSearchQuery(inQuery, onlyLoadResults) {
      const query = lodashCloneDeep(inQuery);
      this.selectedSavedSearch = onlyLoadResults
        ? this.selectedSavedSearch
        : query;
      if (query && query.params) {
        let params = query.params;

        //Get the keyword filter from the saved search
        if (
          lodashGet(params.keywordsBuilder, "includes[0].keywords.length") ||
          lodashGet(params.keywordsBuilder, "excludes.length")
        ) {
          this.keywords = null;
          this.handleChangeKeywordsBuilder(params.keywordsBuilder);
        } else if (params.search) {
          this.keywordsBuilder = null;
          this.handleChangeKeywords(params.search);
        } else {
          this.keywords = null;
          this.keywordsBuilder = null;
        }

        if (params.mapType) {
          this.mapType = params.mapType;
        }

        if (
          params.boundsType === "assets" &&
          ((params.assets && params.assets.length) ||
            (params.places && params.places.length))
        ) {
          // [-49.78357, -21.29712, 10, '158824', 'km']
          const assetIds = [];
          let radius = null;
          let poisDistUnits = null;
          let locs = params.places;
          if (params.assets && params.assets.length) {
            locs = params.assets;
          }
          locs.forEach((asset) => {
            assetIds.push(asset[3]);
            radius = asset[2];
            poisDistUnits = asset[4];
          });
          params.assets = await this.getSelectedAssetsFeatures(assetIds);
          params.radius = radius;
          params.poisDistUnits = poisDistUnits;
          if (lodashHas(params, "places")) {
            delete params.places;
          }
        }

        this.handleChangeLocation(params);

        let _sources = {};
        if (params.sources) {
          if (this.featureUnlocked("livesearch-source-metadata-platform")) {
            lodashForEach(params.sources, (source) => {
              Object.assign(_sources, {
                [source]: true,
              });
            });
          } else {
            _sources = {
              social: params.sources.includes("twitter"),
              news: params.sources.includes("news"),
              political: params.sources.includes("political"),
              maritime: params.sources.includes("maritime"),
              analysis: params.sources.includes("analysis"),
            };
          }
        }
        this.handleChangeSource(_sources);

        this.handleChangeHotspotsFilter({
          hotspotsOnly: query.params.hotspotsOnly || false,
        });
        this.hotspotsOnlySelection = query.params.hotspotsOnly || false;

        this.eventsTypeSelection = query.params.eventType || "";
        this.verifiedTypeSelection = query.params.verifiedType || null;

        if (this.verifiedTypeSelection) {
          this.verifiedExtrasSelection = {
            sectors: query.params.sectors || null,
            veAssets: query.params.veAssets || null,
            attackTypes: query.params.attackTypes || null,
            perpetrators: query.params.perpetrators || null,
          };
        }

        if (params.riskCategories) {
          this.contentType = CONTENT_TYPES.analysis;
          this.riskCategoriesSelection = params.riskCategories.join(",");
          this.handleChangeRiskCategories(this.riskCategoriesSelection);
        }

        if (params.tags) {
          this.contentType = CONTENT_TYPES.analysis;
          this.riskCategoryTagsSelection = params.tags.join(",");
          this.handleChangeRiskCategoryTags(this.riskCategoryTagsSelection);
        }

        if (params.geolocationPrecisions) {
          this.geolocationPrecisions = params.geolocationPrecisions;
          this.handleChangeGeolocationPrecisions(this.geolocationPrecisions);
        }

        if (!isNaN(params.severityMin) || !isNaN(params.severityMax)) {
          this.handleChangeSeverity({
            severityMin: params.severityMin || 0,
            severityMax: params.severityMax || 10,
          });
        }

        if (params.sentiments) {
          this.handleChangeSentiment(params.sentiments.split(","));
        } else {
          this.handleChangeSentiment([]);
        }

        if (params.emotions) {
          this.handleChangeEmotions(params.emotions.split(","));
        } else {
          this.handleChangeEmotions([]);
        }

        this.handleLoadSourceMetadata(params.source_metadata);

        this.boundsType = params.boundsType ? params.boundsType : "geography";
        this.bounds = params.bounds;
        this.handleChangeBounds();

        // Move to map location
        if (this.map && params.bounds) {
          let _bounds = params.bounds.split(",");
          try {
            this.map.fitBounds([
              [_bounds[1], _bounds[0]],
              [_bounds[3], _bounds[2]],
            ]);
          } catch (error) {
            console.error(error);
          }
        }

        if (
          lodashGet(this.$route, "query.start") &&
          lodashGet(this.$route, "query.end")
        ) {
          params.start = this.$route.query.start;
          params.end = this.$route.query.end;

          this.handleChangeDateRange({
            range: convertDatesToRange(params.start, params.end),
            start: params.start,
            end: params.end,
          });
        } else if (params.start && params.end) {
          this.handleChangeDateRange({
            range: convertDatesToRange(params.start, params.end),
            start: params.start,
            end: params.end,
          });
        }

        this.handleChangeEventsV2({
          eventType: params.eventType || null,
          events:
            (Array.isArray(params.topics)
              ? params.topics.join(",")
              : params.topics) || null,
          sectors: params.sectors || null,
          veAssets: params.veAssets || null,
          severity: params.severity || null,
          attackTypes: params.attackTypes || null,
          perpetrators: params.perpetrators || null,
          maritimeSubcategories: params.maritimeSubcategories || null,
          maritimeVesselTypes: params.maritimeVesselTypes || null,
          verifiedMapType: params.verifiedMapType || null,
          mapType: params.mapType || null,
        });

        if (!onlyLoadResults) {
          this.handlePageTrackerEvent(
            SEARCH_CATEGORIES.live_search_saved_queries,
            EVENT_ACTIONS.clicked,
            query[NOTIFICATION_ID_PROPERTY]
          );
          this.$emit(EMITTED_ACTIONS.CHANGE_SAVED_SEARCH_QUERY, query);
        }
      } else {
        this.$emit(EMITTED_ACTIONS.RESET, {});
      }
    },
    constructEventsSelectionFromString(topicsString) {
      let topicApiNames = topicsString ? topicsString.split(",") : [];
      let eventsSelection = {};
      for (let i = 0; i < topicApiNames.length; i++) {
        const cur = topicApiNames[i];
        let topic = findTopicAllFlatten(cur, "apiName");
        if (!topic.parentIds) {
          if (!eventsSelection[topic.apiName]) {
            eventsSelection[topic.apiName] = {
              selected: true,
              apiName: topic.apiName,
              subtopics: [],
            };
          }
          eventsSelection[topic.apiName].selected = true;
        } else {
          let parentTopicKey = topic.parentIds[0];
          if (!eventsSelection[parentTopicKey]) {
            eventsSelection[parentTopicKey] = {
              apiName: topic.parentIds[0],
              subtopics: [],
            };
          }
          eventsSelection[parentTopicKey].subtopics.push(topic.apiName);
        }
      }
      return eventsSelection;
    },
    handleChangeEventType(params) {
      this.$emit(EMITTED_ACTIONS.CHANGE_TAB, params);
    },
    /**
     * Generates a natural language explanation of the Date Range filter
     * @returns {string}
     */
    getDateRangeString() {
      let numDays;

      //Add 1 to the time range since timeRange is inclusive
      //e.g. timeRange=[-29,-27] == 3 days --> 27 days from today to 29 days from today is 3 total days
      if (this.timeRange) {
        numDays = this.timeRange[1] - this.timeRange[0] + 1;
      } else {
        numDays = this.dateRangeStartDaysBack + 1;
      }

      return numDays + (numDays === 1 ? " day" : " days");
    },
    handleChangeRiskCategories(params) {
      this.riskCategoriesSelection = params;
      this.$emit(EMITTED_ACTIONS.CHANGE_RISK_CATEGORIES, params);
      const size = params.split(",").length;
      if (size > 1) {
        this.handlePageTrackerEvent(
          SEARCH_CATEGORIES.live_search_risk_categories,
          EVENT_ACTIONS.clicked,
          params
        );
      }
    },
    handleChangeKeywordsBuilder(keywordsBuilder) {
      this.keywordsBuilder = keywordsBuilder;
      this.$emit(EMITTED_ACTIONS.CHANGE_KEYWORDS_BUILDER, keywordsBuilder);
    },
    handleChangeSeverity(severity, dontSendEmit) {
      this.severity = severity;
      if (!dontSendEmit) {
        this.$emit(EMITTED_ACTIONS.CHANGE_SEVERITY, severity);
      }
    },
    handleChangeSentiment(sentiment, dontSendEmit) {
      this.sentiment = sentiment;
      if (!dontSendEmit) {
        this.$emit(EMITTED_ACTIONS.CHANGE_SENTIMENT, sentiment);
      }
    },
    handleChangeEmotions(emotions, dontSendEmit) {
      this.emotions = emotions;
      if (!dontSendEmit) {
        this.$emit(EMITTED_ACTIONS.CHANGE_EMOTIONS, emotions);
      }
    },
    handleLoadSourceMetadata(sourceMetadata, dontSendEmit = false) {
      const sourceMetadataArr = [];
      SOURCE_METADATA.forEach((type) => {
        let value = [];
        if (sourceMetadata?.[type]) {
          value = sourceMetadata[type];
        }
        sourceMetadataArr.push({ type, value });
      });
      if (sourceMetadataArr.length) {
        this.handleChangeSourceMetadata(sourceMetadataArr, dontSendEmit, true);
      }
    },
    handleChangeSourceMetadata(arr, dontSendEmit, dontTrackEmptyValue) {
      if (Array.isArray(arr)) {
        arr.forEach((item) => {
          if (
            this.isSourceMetadataTypeFeatureUnlocked(item.type) &&
            item.type &&
            item.value
          ) {
            this.sourceMetadata[item.type] = item.value;
            const valueText = Array.isArray(item.value)
              ? item.value.join()
              : item.value.toString();
            if (valueText?.length || !dontTrackEmptyValue) {
              this.handlePageTrackerEvent(
                SEARCH_CATEGORIES[`live_search_source_metadata`],
                EVENT_ACTIONS.clicked,
                `${item.type}: ${valueText}`
              );
            }
          }
        });
        if (!dontSendEmit) {
          this.$emit(
            EMITTED_ACTIONS.CHANGE_SOURCE_METADATA,
            this.sourceMetadata
          );
        }
      }
    },
    handleChangeGeolocationPrecisions(geolocationPrecisions) {
      this.geolocationPrecisionsSelection = geolocationPrecisions;
      this.$emit(
        EMITTED_ACTIONS.CHANGE_GEOLOCATION_PRECISIONS,
        geolocationPrecisions
      );
    },
    handleChangeRiskCategoryTags(params) {
      this.riskCategoryTagsSelection = params;
      this.$emit(
        EMITTED_ACTIONS.CHANGE_RISK_CATEGORY_TAGS,
        this.riskCategoryTagsSelection
      );
      const size = this.riskCategoryTagsSelection
        ? this.riskCategoryTagsSelection.split(",").length
        : 0;
      if (size >= 1) {
        this.handlePageTrackerEvent(
          SEARCH_CATEGORIES.live_search_risk_category_tags,
          EVENT_ACTIONS.clicked,
          params
        );
      }
    },
    handleClickClear() {
      this.$emit(EMITTED_ACTIONS.CLICK_CLEAR);
    },
    handleClickShowLibrary() {
      this.$emit(EMITTED_ACTIONS.SHOW_LIBRARY);
    },
  },
  watch: {
    /**
     * The last time the search was reset. When the timestamp changes, all search filters watching it will reset themselves
     */
    savedSearchDetails: {
      handler() {
        this.handleChangeSavedSearchQuery(this.savedSearchDetails);
      },
      deep: true,
    },
    resetTimestamp: {
      handler() {
        this.reset();
      },
    },
    hasFiltersSet: {
      handler() {
        this.$emit(EMITTED_ACTIONS.CHANGE_HAS_FILTERS_SET, this.hasFiltersSet);
      },
    },
    currentContentType: {
      handler(newVal) {
        this.contentType = newVal;
      },
    },
    contentType: {
      handler(newVal) {
        if (newVal === CONTENT_TYPES.events) {
          this.riskCategoriesSelection = null;
          this.riskCategoryTagsSelection = null;
        } else {
          this.eventsSelection = null;
        }
      },
    },
    shouldPullData: {
      handler() {
        this.$emit(
          EMITTED_ACTIONS.CHANGE_SHOULD_PULL_DATA,
          this.shouldPullData
        );
      },
    },
    selectedSearchParams: {
      handler(newValue, oldValue) {
        if (newValue && oldValue !== newValue) {
          this.selectedSavedSearch = null;
          lodashDebounce(() => {
            this.handleChangeSavedSearchQuery({ params: newValue }, true);
          }, 1000)();
        }
      },
      deep: true,
    },
    "selectorPanel.searchWithAssetRadius": {
      async handler() {
        if (this.selectorPanel.searchWithAssetRadius) {
          this.loadingPrefs = true;
          this.reset(true);
          lodashDebounce(async () => {
            await searchWithAssetRadius(
              this.selectorPanel.searchWithAssetRadius
            );
            this.handleChangeDateRange(
              {
                range: convertDatesToRange(
                  this.preferences?.liveSearch?.start,
                  this.preferences?.liveSearch?.end
                ),
                start: this.preferences?.liveSearch?.start,
                end: this.preferences?.liveSearch?.end,
              },
              true
            );
            await this.setSelectionsWithPreferences();
            this.$emit(EMITTED_ACTIONS.CHANGE_SHOULD_PULL_DATA, true);
            this.loadingPrefs = false;
          }, 2000)();
        }
      },
      deep: true,
    },
    // handle force opening selectorPanel to specific saved search
    "selectorPanel.selectedData.savedSearchId": {
      handler() {
        if (
          this.selectorPanel?.selected &&
          this.selectorPanel.selected === "search"
        ) {
          if (lodashGet(this.selectorPanel, "selectedData.savedSearchId")) {
            this.rememberedSavedSearchId = this.selectorPanel.selectedData.savedSearchId;
            this.$store.dispatch(
              MODULE_PREFIX_SAVED_SEARCH + GET_SAVED_SEARCH_DETAILS_BY_ID,
              this.rememberedSavedSearchId
            );
            this.$emit(EMITTED_ACTIONS.CHANGE_HAS_FILTERS_SET, true);
          } else {
            this.$emit(EMITTED_ACTIONS.RESET, {});
          }
        }
      },
    },
    selectedData() {
      if (lodashGet(this.selectedData, "showCountryRiskSelections")) {
        this.$emit(EMITTED_ACTIONS.RESET, {});
        lodashDebounce(() => {
          this.boundsType = "geography";
          this.$emit(EMITTED_ACTIONS.CHANGE_BOUNDS, this.boundsType);
          this.locationSelectionAoiId = [this.selectedData.isoCode];
          this.$emit(EMITTED_ACTIONS.CHANGE_LOCATIONS, {
            aoiId: this.locationSelectionAoiId,
          });
          if (lodashGet(this.selectedData, "riskCategoriesSelection")) {
            this.riskCategoriesSelection = this.selectedData.riskCategoriesSelection;
            this.$emit(
              EMITTED_ACTIONS.CHANGE_RISK_CATEGORIES,
              this.riskCategoriesSelection
            );
          }
          const start = getTodayStartEndDate(dayjs.utc().add(29, "days")).start;
          const end = getTodayStartEndDate(dayjs.utc()).end;
          this.handleChangeDateRange({
            range: convertDatesToRange(start, end),
            start: start,
            end: end,
          });
        }, 2000)();
      }
    },
    loadingQueries() {
      if (!this.loadingQueries) {
        this.queriesHaveBeenLoaded = true;
      }
    },
    lockFacetPanels() {
      if (
        !(
          this.loadingQueries ||
          this.loadingPrefs ||
          !this.queriesHaveBeenLoaded
        )
      ) {
        this.filterPanelsOpen = 1;
      }
    },
  },
};
</script>

<style>
.search-box {
  font-size: 14px;
}
.search-box .v-icon {
  font-size: 14px !important;
}
.search-box.v-text-field.v-text-field--enclosed .v-text-field__details {
  display: none;
}
.search-box.theme--light.v-text-field--solo-inverted.v-input--is-focused
  > .v-input__control
  > .v-input__slot {
  background-color: rgba(0, 0, 0, 0.06);
}
.search-box.theme--light.v-text-field--solo-inverted.v-input--is-focused
  > .v-input__control
  > .v-input__slot
  input {
  color: rgba(0, 0, 0, 0.6);
}
.search-box.theme--light.v-text-field--solo-inverted.v-input--is-focused,
> .v-input__control,
> .v-input__slot,
.v-label {
  color: rgba(0, 0, 0, 0.6);
}
.bounds-radio-text .v-input--selection-controls .v-input__slot > .v-label,
.v-input--selection-controls .v-radio > .v-label {
  font-size: 12px;
}
.search-box * .v-input__slot {
  padding-left: 4px !important;
  padding-right: 4px !important;
}
</style>

<style scoped>
.hyp-main-container {
  overflow-y: auto;
  position: relative;
  width: 100%;
}
.hyp-exp-panels {
  margin-bottom: 75px;
}
.panel-header {
  height: 60px;
}
.panel-header-text {
  color: #333;
}
.panel-header-icon {
  height: 30px;
  width: 30px;
}
.v-radio >>> .v-label {
  color: #1c1c1c;
}
.reset-filters-icon {
  float: right;
  padding-top: 5px;
  padding-left: 5px;
}
::v-deep .v-badge__badge {
  height: 12px;
  min-width: 12px;
}
::v-deep .date-range .v-badge__badge {
  min-height: 12px;
  height: auto;
}
.panel-toggle {
  display: inline-block;
}
::v-deep .panel-toggle.v-input--is-readonly .v-input--selection-controls__input,
::v-deep
  .panel-toggle.v-input--is-readonly
  .v-input--selection-controls__input
  input {
  cursor: default;
}
::v-deep
  .panel-toggle.v-input--is-readonly
  .v-input--selection-controls__ripple {
  display: none;
}
.switch-w-tooltip {
  width: min-content;
  display: inline-block;
}
.bottom-controls-container {
  background-color: white;
  position: absolute;
  bottom: 0;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  left: 0;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  width: 321px;
  height: 56px;
  -webkit-box-shadow: 0px 2px 4px -1px rgba(0, 0, 0, 0.2),
    0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12);
  box-shadow: 0px 2px 4px -1px rgba(0, 0, 0, 0.2),
    0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12);
}
.hyp-main-panels-container {
  max-height: calc(100vh - 190px);
  overflow-y: auto;
}
</style>
